import React, { useMemo } from 'react'
import DateVO from '../../../../../../../../../vo/DateVO'
import { GanttCellRendererProps } from './index'
import { GanttColor } from '../../../../../../../../../styles/commonStyles'
import { WbsItemStatus } from '../../wbsItemStatus'
import objects from '../../../../../../../../../utils/objects'
import { GanttDisplayUnit } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import { MilestoneDetail } from '../../../../../../../../../domain/entity/MilestoneEntity'
import { isGanttSummaryType } from '../ganttUtil'

const Colors = {
  // Common
  GRID_LINE: '#d8dde5',
  GRID_LINE_LIGHT: colorPalette.monotone[2] + '66',
  TODAY_BACKGROUND: '#fdf3ea',
  TODAY_BORDER: GanttColor.BORDER_TODAY,
  HOLIDAY_BACKGROUND: '#d8dde526',
  // Legacy (v1)
  WARNING_BACKGROUND: '#fffacd',
  SCHEDULED: '#5bb9ff',
  SCHEDULED_BORDER: '#5bb9ff',
  LATE: '#f486b0',
  LATE_BORDER: '#f486b0',
  DONE: '#f7f8fa',
  DONE_BORDER: '#d8dde5',
  // V2
  ACTUAL_NORMAL: colorPalette.monotone[4],
  ACTUAL_NORMAL_BORDER: colorPalette.monotone[3],
  ACTUAL_LATE: colorPalette.pink[3],
  ACTUAL_LATE_BORDER: colorPalette.pink[4],
  ACTUAL_DONE: colorPalette.monotone[2],
  ACTUAL_DONE_BORDER: colorPalette.monotone[2],
} as const

export type DatePosition = {
  x: number
  width: number
  date: CalendarDateVO
}

interface Props extends GanttCellRendererProps {}

export const GanttBackgroundCanvas = React.memo((props: Props) => {
  const {
    data,
    dataPath,
    timeScale,
    parameter,
    displayTimeScale,
    milestones,
    isV2,
  } = props
  const prefix = dataPath ? `${dataPath}.` : ''
  const wbsItem = objects.getValue(data, `${prefix}wbsItem`)
  const cumulation = objects.getValue(data, `${prefix}cumulation`)

  const status = wbsItem?.status
  const scheduledDate = wbsItem?.scheduledDate
  const actualDate = wbsItem?.actualDate
  const hasValidData = !!(status && scheduledDate && actualDate)

  if (!hasValidData) {
    return <div className="sevend-ag-cell-gantt-chart" />
  }

  return (
    <GanttChart
      status={status}
      scheduledDate={scheduledDate}
      actualDate={actualDate}
      cumulation={cumulation}
      timeScale={timeScale}
      displayTimeScale={displayTimeScale}
      unit={parameter.unit}
      milestones={milestones}
      isV2={isV2}
      isSummaryRow={isGanttSummaryType(wbsItem?.wbsItemType)}
    />
  )
})

GanttBackgroundCanvas.displayName = 'GanttBackgroundCanvas'

type GanttChartProps = {
  status: WbsItemStatus
  scheduledDate: { startDate?: Date; endDate?: Date }
  actualDate: { startDate?: Date; endDate?: Date }
  cumulation?: { maxScheduledEndDate?: Date }
  timeScale: CalendarDateVO[]
  displayTimeScale: CalendarDateVO[]
  unit: GanttDisplayUnit
  milestones?: MilestoneDetail[]
  isV2?: boolean
  isSummaryRow: boolean
}

const GanttChart = React.memo((props: GanttChartProps) => {
  const {
    status,
    scheduledDate,
    actualDate,
    cumulation,
    timeScale,
    displayTimeScale,
    unit,
    milestones,
    isV2,
    isSummaryRow,
  } = props

  const datePositions = useDatePositions(timeScale, unit)
  const linearDatePositions = useLinearDatePositions(timeScale)

  const dateInfo = useMemo(() => {
    const now = new Date()
    return {
      wbsIsDoneOrDiscard: [WbsItemStatus.DONE, WbsItemStatus.DISCARD].includes(
        status
      ),
      wbsIsReview: status === WbsItemStatus.REVIEW,
      scheduledStart: scheduledDate.startDate
        ? CalendarDateVO.of(scheduledDate.startDate)
        : undefined,
      scheduledEnd: scheduledDate.endDate
        ? CalendarDateVO.of(scheduledDate.endDate)
        : undefined,
      actualStart: actualDate.startDate
        ? CalendarDateVO.of(actualDate.startDate)
        : undefined,
      actualEnd: actualDate.endDate
        ? CalendarDateVO.of(actualDate.endDate)
        : undefined,
      today: CalendarDateVO.of(now),
      endOfToday: CalendarDateVO.of(new DateVO(now).addDays(1).toDate()),
      maxScheduledDate: cumulation?.maxScheduledEndDate
        ? CalendarDateVO.of(cumulation.maxScheduledEndDate)
        : undefined,
    }
  }, [status, scheduledDate, actualDate, cumulation])

  const backgrounds = useBackgrounds(datePositions, dateInfo, unit, isV2)
  const gridLines = useGridLines(datePositions, unit, displayTimeScale)
  const todayLine = useTodayLine(linearDatePositions, dateInfo.endOfToday)
  const bars = useBars(linearDatePositions, dateInfo, isV2, isSummaryRow)
  const milestoneLines = useMilestoneLines(datePositions, milestones)

  return (
    <div
      className="sevend-ag-cell-gantt-chart"
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
        preserveAspectRatio="none"
      >
        <g className="gantt-backgrounds">{backgrounds}</g>
        <g className="gantt-grid-lines">{gridLines}</g>
        <g className="gantt-today-line">{todayLine}</g>
        <g className="gantt-milestones">{milestoneLines}</g>
        <g className="gantt-bars">{bars}</g>
      </svg>
    </div>
  )
})

GanttChart.displayName = 'GanttChart'

// Custom hooks

export const useDatePositions = (
  timeScale: CalendarDateVO[],
  unit: GanttDisplayUnit
): DatePosition[] => {
  return useMemo(() => {
    const isMonthUnit = unit === GanttDisplayUnit.MONTH

    if (!isMonthUnit) {
      const width = 100 / timeScale.length
      return timeScale.map((date, i) => ({ x: i * width, width, date }))
    }

    const monthDaysCache = new Map<string, number>()
    const getMonthDays = (date: CalendarDateVO): number => {
      const key = `${date.date.getFullYear()}-${date.date.getMonth()}`
      if (!monthDaysCache.has(key)) {
        monthDaysCache.set(
          key,
          new Date(
            date.date.getFullYear(),
            date.date.getMonth() + 1,
            0
          ).getDate()
        )
      }
      return monthDaysCache.get(key)!
    }

    const uniqueMonths = new Set(
      timeScale.map(d => `${d.date.getFullYear()}-${d.date.getMonth()}`)
    )
    const monthWidth = 100 / uniqueMonths.size

    const positions: DatePosition[] = []
    let currentX = 0
    for (const date of timeScale) {
      const width = monthWidth / getMonthDays(date)
      positions.push({ x: currentX, width, date })
      currentX += width
    }
    return positions
  }, [timeScale, unit])
}

/** Slider-compatible linear positions (for bars that must align with MUI Slider) */
export const useLinearDatePositions = (
  timeScale: CalendarDateVO[]
): DatePosition[] => {
  return useMemo(() => {
    const width = 100 / timeScale.length
    return timeScale.map((date, i) => ({ x: i * width, width, date }))
  }, [timeScale])
}

const useBackgrounds = (
  datePositions: DatePosition[],
  dateInfo: {
    today: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    maxScheduledDate?: CalendarDateVO
  },
  unit: GanttDisplayUnit,
  isV2?: boolean
): React.ReactNode[] => {
  return useMemo(() => {
    const { today, scheduledEnd, maxScheduledDate } = dateInfo
    const backgrounds: React.ReactNode[] = []

    for (let i = 0; i < datePositions.length; i++) {
      const { x, width, date } = datePositions[i]
      const isToday = date.isEqual(today)

      if (isToday) {
        backgrounds.push(
          <rect
            key={`today-${i}`}
            x={`${x}%`}
            y="0"
            width={`${width}%`}
            height="100%"
            fill={Colors.TODAY_BACKGROUND}
          />
        )
      } else if (unit === GanttDisplayUnit.DAY && date.isHoliday()) {
        backgrounds.push(
          <rect
            key={`holiday-${i}`}
            x={`${x}%`}
            y="0"
            width={`${width}%`}
            height="100%"
            fill={Colors.HOLIDAY_BACKGROUND}
          />
        )
      }

      // Legacy: warning background for overrun area
      if (
        !isV2 &&
        scheduledEnd &&
        maxScheduledDate &&
        date.isAfter(scheduledEnd) &&
        !date.isAfter(maxScheduledDate)
      ) {
        backgrounds.push(
          <rect
            key={`warning-${i}`}
            x={`${x}%`}
            y="25%"
            width={`${width}%`}
            height="50%"
            fill={Colors.WARNING_BACKGROUND}
          />
        )
      }
    }
    return backgrounds
  }, [datePositions, dateInfo, unit, isV2])
}

export const useOverrunPosition = (
  datePositions: DatePosition[],
  scheduledEndDate?: Date,
  maxScheduledEndDate?: Date
): { left: number; width: number } | null => {
  return useMemo(() => {
    if (
      !scheduledEndDate ||
      !maxScheduledEndDate ||
      datePositions.length === 0
    ) {
      return null
    }

    const scheduledEnd = CalendarDateVO.of(scheduledEndDate)
    const maxScheduledDate = CalendarDateVO.of(maxScheduledEndDate)

    const startPos = datePositions.find(p => p.date.isEqual(scheduledEnd))
    const endPos = datePositions.find(p => p.date.isEqual(maxScheduledDate))

    const firstDate = datePositions[0].date
    const lastDate = datePositions[datePositions.length - 1].date

    // Both out of range: only show if overrun spans the entire display range
    if (!startPos && !endPos) {
      if (
        scheduledEnd.isBefore(firstDate) &&
        maxScheduledDate.isAfter(lastDate)
      ) {
        return { left: 0, width: 100 }
      }
      return null
    }

    // Determine left edge: fallback to 0% only if scheduledEnd is before range
    let left: number
    if (startPos) {
      left = startPos.x + startPos.width
    } else if (scheduledEnd.isBefore(firstDate)) {
      left = 0
    } else {
      return null // scheduledEnd is after range: overrun not visible
    }

    // Determine right edge: fallback to 100% only if maxScheduledDate is after range
    let right: number
    if (endPos) {
      right = endPos.x + endPos.width
    } else if (maxScheduledDate.isAfter(lastDate)) {
      right = 100
    } else {
      return null // maxScheduledDate is before range: overrun not visible
    }

    const width = right - left
    if (width <= 0) return null

    return { left, width }
  }, [datePositions, scheduledEndDate, maxScheduledEndDate])
}

const useGridLines = (
  datePositions: DatePosition[],
  unit: GanttDisplayUnit,
  displayTimeScale: CalendarDateVO[]
): React.ReactNode[] => {
  return useMemo(() => {
    const isDayUnit = unit === GanttDisplayUnit.DAY
    const isMonthUnit = unit === GanttDisplayUnit.MONTH
    const isWeekUnit = unit === GanttDisplayUnit.WEEK

    const gridLines: React.ReactNode[] = []

    for (let i = 1; i < datePositions.length; i++) {
      const { x, date } = datePositions[i]
      const matchScale =
        isDayUnit || displayTimeScale.some(v => v.isEqual(date))

      if (!matchScale) continue

      const isFirstday = !isMonthUnit && date.isFirstday()
      const isFirstmonth = isMonthUnit && date.isFirstmonth()
      const isFirstweek = isWeekUnit && date.isFirstweek()
      const isStrong = isFirstday || isFirstmonth || isFirstweek

      gridLines.push(
        <line
          key={`grid-${i}`}
          x1={`${x}%`}
          y1="0"
          x2={`${x}%`}
          y2="100%"
          stroke={isStrong ? Colors.GRID_LINE : Colors.GRID_LINE_LIGHT}
          strokeWidth="1"
        />
      )
    }
    return gridLines
  }, [datePositions, unit, displayTimeScale])
}

const useTodayLine = (
  datePositions: DatePosition[],
  endOfToday: CalendarDateVO
): React.ReactNode | null => {
  return useMemo(() => {
    const todayPos = datePositions.find(p => p.date.isEqual(endOfToday))
    if (!todayPos) return null
    const todayX = todayPos.x - todayPos.width / 2
    return (
      <line
        key="today-line"
        x1={`${todayX}%`}
        y1="0"
        x2={`${todayX}%`}
        y2="100%"
        stroke={Colors.TODAY_BORDER}
        strokeWidth="2"
      />
    )
  }, [datePositions, endOfToday])
}

function getActualBarColors(
  isDone: boolean,
  isLate: boolean,
  isV2: boolean
): { fill: string; border: string } {
  if (isDone) {
    return isV2
      ? { fill: Colors.ACTUAL_DONE, border: Colors.ACTUAL_DONE_BORDER }
      : { fill: Colors.DONE, border: Colors.DONE_BORDER }
  }
  if (isLate) {
    return isV2
      ? { fill: Colors.ACTUAL_LATE, border: Colors.ACTUAL_LATE_BORDER }
      : { fill: Colors.LATE, border: Colors.LATE_BORDER }
  }
  return isV2
    ? { fill: Colors.ACTUAL_NORMAL, border: Colors.ACTUAL_NORMAL_BORDER }
    : { fill: Colors.SCHEDULED, border: Colors.SCHEDULED_BORDER }
}

const useBars = (
  datePositions: DatePosition[],
  dateInfo: {
    wbsIsDoneOrDiscard: boolean
    wbsIsReview: boolean
    scheduledStart?: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    actualStart?: CalendarDateVO
    actualEnd?: CalendarDateVO
    endOfToday: CalendarDateVO
  },
  isV2?: boolean,
  isSummaryRow?: boolean
): React.ReactNode[] => {
  return useMemo(() => {
    const {
      wbsIsDoneOrDiscard,
      wbsIsReview,
      scheduledStart,
      scheduledEnd,
      actualStart,
      actualEnd,
      endOfToday,
    } = dateInfo

    const bars: React.ReactNode[] = []

    // V2: 末端タイプのみ実績バーを描画（親タイプは skipActualBars で除外）
    // SVG y="50%"=行中心 からのオフセットで実績バー位置を決定
    // 予定バー中心 = 行中心 - setShift
    // 実績バー top = 予定バー bottom + gap = 行中心 - setShift + barHeight/2 + gap
    // SVG offset = barHeight/2 - setShift + gap
    const v2BarHeightPx = 3
    const barHeight = isSummaryRow ? 32 : 20
    const gap = 4
    const setShift = (gap + v2BarHeightPx) / 2
    const v2BarOffsetPx = barHeight / 2 - setShift + gap

    // Legacy: percentage-based sizing
    const legacyBarY = 25
    const legacyBarHeight = 50

    const strokeWidth = isV2 ? 1 : 2
    const rx = isV2 ? 2 : 4

    const findPosition = (targetDate: CalendarDateVO) =>
      datePositions.find(p => p.date.isEqual(targetDate))

    // V2: 親タイプ（WG/PROCESS/DL）は実績バーを描画しない
    const skipActualBars = isV2 && isSummaryRow

    // Late bar
    if (
      !skipActualBars &&
      !actualStart &&
      !actualEnd &&
      scheduledStart?.isBefore(endOfToday)
    ) {
      const startPos = findPosition(scheduledStart)
      const endPos = findPosition(endOfToday)
      if (startPos && endPos && endPos.x > startPos.x) {
        const lateColor = isV2 ? Colors.ACTUAL_LATE : Colors.LATE
        const lateBorder = isV2 ? Colors.ACTUAL_LATE_BORDER : Colors.LATE_BORDER
        bars.push(
          <rect
            key="late-bar"
            x={`${startPos.x}%`}
            y={isV2 ? '50%' : `${legacyBarY}%`}
            width={`${endPos.x - startPos.x}%`}
            height={isV2 ? v2BarHeightPx : `${legacyBarHeight}%`}
            transform={isV2 ? `translate(0, ${v2BarOffsetPx})` : undefined}
            fill={lateColor}
            stroke={lateBorder}
            strokeWidth={strokeWidth}
            rx={rx}
          />
        )
      }
    }

    // Actual result bar
    if (!skipActualBars && actualStart) {
      const startPos = findPosition(actualStart)
      if (startPos) {
        const displayEnd = actualEnd || endOfToday
        const endPos = findPosition(displayEnd)
        const barEndX = endPos
          ? actualEnd
            ? endPos.x + endPos.width
            : endPos.x
          : 100
        const barWidth = barEndX - startPos.x

        if (barWidth > 0) {
          const isLate = scheduledEnd && displayEnd.isAfter(scheduledEnd)
          const isDone = wbsIsDoneOrDiscard || (wbsIsReview && actualEnd)
          const { fill: fillColor, border: borderColor } = getActualBarColors(
            !!isDone,
            !!isLate,
            !!isV2
          )

          bars.push(
            <rect
              key="actual-bar"
              x={`${startPos.x}%`}
              y={isV2 ? '50%' : `${legacyBarY}%`}
              width={`${barWidth}%`}
              height={isV2 ? v2BarHeightPx : `${legacyBarHeight}%`}
              transform={isV2 ? `translate(0, ${v2BarOffsetPx})` : undefined}
              fill={fillColor}
              stroke={borderColor}
              strokeWidth={strokeWidth}
              rx={rx}
            />
          )
        }
      }
    }

    return bars
  }, [datePositions, dateInfo, isV2, isSummaryRow])
}

const useMilestoneLines = (
  datePositions: DatePosition[],
  milestones?: MilestoneDetail[]
): React.ReactNode[] => {
  return useMemo(() => {
    if (!milestones || milestones.length === 0) return []

    const lines: React.ReactNode[] = []

    const findPosition = (targetDate: CalendarDateVO) =>
      datePositions.find(p => p.date.isEqual(targetDate))

    const groupedByDate = new Map<string, MilestoneDetail[]>()
    for (const milestone of milestones) {
      const dateKey = milestone.milestoneDate
      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, [])
      }
      groupedByDate.get(dateKey)!.push(milestone)
    }

    for (const [dateKey, groupMilestones] of groupedByDate) {
      const milestoneDate = CalendarDateVO.of(new Date(dateKey))
      const pos = findPosition(milestoneDate)

      if (pos) {
        const firstMilestone = groupMilestones[0]
        const color = firstMilestone.color
        const xPos = pos.x + pos.width / 2

        lines.push(
          <line
            key={`milestone-${dateKey}`}
            x1={`${xPos}%`}
            y1="0"
            x2={`${xPos}%`}
            y2="100%"
            stroke={color}
            strokeWidth="2"
          />
        )
      }
    }

    return lines
  }, [datePositions, milestones])
}
